import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { BookingContext } from "../../context/BookingContext";
import { Types as SearchTypes } from "../../context/search.reducer";
import { Types as RequestTypes } from "../../context/request.reducer";
import { Types as SavedRequestTypes } from "../../context/savedRequest.reducer";
import { Classroom, ClassroomOrderByFields, PageInfo } from "@models/ISchema";
import useClassroomBookingsMutation from "../../hooks/useClassroomBookingsMutation";
import {
  CardNotification,
  Pager,
  RoundButton,
  Table,
  TableColumn,
  toast,
  Tooltip,
} from "@foris/avocado-suite";
import { getColumnCell, getPillCell } from "@common/utils/table.util";
import { BookingClassroomOrderPayload } from "@context/base.reducer";
import { useTranslation } from "react-i18next";
import { DayRowData } from "@modules/booking/context/types";
import DimensionsSearchSkeleton from "@components/dimensions-search-skeleton";
import useBookingContextUrl from "@modules/booking/hooks/useBookingContextUrl";
import css from "./cardBooking.module.scss";
import { getDateKey } from "@utils/time";
import ClassroomCell from "../classroom-cell";

interface ICardResultsProps {
  items: Classroom[];
  isLoadingItems?: boolean;
  orderBy?: BookingClassroomOrderPayload;
  pageInfo?: PageInfo;
  pageSize?: number;
  onChangePage?: (page: number) => void;
  onChangePageSize?: (pageSize: number) => void;
  onChangeOrderBy?: (orderBy: BookingClassroomOrderPayload) => void;
  onSelectClassroom?: (classroom: Classroom) => void;
}

const getCoveragePillColors = (coveragePercentage: number) => {
  if (coveragePercentage >= 100) {
    return {
      color: "success-opacity" as const,
      textColor: "success-60" as const,
    };
  }

  if (coveragePercentage >= 50) {
    return {
      color: "warning-opacity" as const,
      textColor: "warning-60" as const,
    };
  }

  return {
    color: "error-opacity" as const,
    textColor: "error-60" as const,
  };
};

const getClassroomCoveredDates = (classroom: Classroom, dates: DayRowData[] = []) => {
  const classroomDates = new Map<string, boolean>();

  classroom?.dateAvailability?.forEach(date => {
    if (date?.isAvailable) {
      const dateKey = getDateKey(date?.date, date?.startTime, date?.endTime);

      classroomDates.set(dateKey, true);
    }
  });

  return [...dates].filter(date => {
    const dateKey = getDateKey(
      date?.day?.dateValue,
      date?.start?.value?.value,
      date?.end?.value?.value,
    );

    return classroomDates.has(dateKey);
  });
};

const CardBooking: React.FC<ICardResultsProps> = ({
  items = [],
  orderBy = [],
  isLoadingItems,
  pageInfo,
  pageSize,
  onChangePage,
  onChangePageSize,
  onChangeOrderBy,
  onSelectClassroom,
}: ICardResultsProps) => {
  const history = useHistory();
  const { origin, scenario, contextUrl, isAdminBooking } = useBookingContextUrl();
  const { state, dispatch } = useContext(BookingContext);
  const [bookingId, setBookingId] = useState<string>(null);
  const [ignoreMutationResult, setIgnoreMutationResult] = useState<boolean>(false);
  const [mutationResult, submitClassroomBookingsMutation] = useClassroomBookingsMutation({
    origin,
    scenario,
  });
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();

  const isLoadingRequestOrItems = isLoading || isLoadingItems;

  const selectedClassroomsById = useMemo(() => {
    return state?.search?.selectedClassrooms?.reduce((acc, classroom) => {
      acc[classroom.classroom.id] = classroom;
      return acc;
    }, {});
  }, [state?.search?.selectedClassrooms]);

  useEffect(() => {
    if (Boolean(state?.search?.currentBooking)) {
      setBookingId(state?.search?.currentBooking?.id);
    }
  }, []);

  useEffect(() => {
    if (ignoreMutationResult) {
      return;
    }

    const { result, error } = mutationResult;

    if (result != null && Object.keys(result).length === 0 && !error) return;

    const booking = result?.payload?.updates[0]?.booking;

    if (!error) {
      if (Boolean(booking)) {
        setIgnoreMutationResult(true);

        // cleaning
        dispatch({ type: SavedRequestTypes.clearSavedData, payload: null });
        dispatch({ type: RequestTypes.ClearRequest, payload: null });
        dispatch({ type: SearchTypes.SetView, payload: "search" });

        dispatch({ type: SearchTypes.SetCurrentBooking, payload: booking });
        dispatch({ type: SearchTypes.ClearSearch, payload: null });
        dispatch({
          type: RequestTypes.SetSnackbar,
          payload: { display: true, message: "Sala actualizada exitosamente", error: false },
        });
        dispatch({
          type: RequestTypes.SetUserResponsible,
          payload: { value: booking?.responsible?.id, error: false },
        });
        dispatch({
          type: RequestTypes.SetEventType,
          payload: { value: booking?.type?.id, error: false },
        });

        // redirect to the created booking (remember that booking updates
        // deletes the edited booking and creates a new one)
        history.push(`/booking/detail/${contextUrl}/${booking?.id}`);
      }
    } else {
      const bookingId = state?.search?.currentBooking?.id;
      setIgnoreMutationResult(true);

      toast.error(t("booking.classroom-search.notifications.general-error"), {
        autoClose: 3000,
        toastId: "general-error-booking",
      });

      dispatch({ type: SearchTypes.ClearSearch, payload: null });
      dispatch({ type: RequestTypes.ClearRequest, payload: null });
      dispatch({ type: SavedRequestTypes.clearSavedData, payload: null });

      history.push(`/booking/detail/${contextUrl}/${bookingId}`);
    }
  }, [mutationResult]);

  const handleSubmitRequest = async (classroom: Classroom) => {
    setIsLoading(true);

    const booking = {
      ...state?.request?.editedBooking,
      /**
       * Only for full coverage bookings, we need to set the classroom to the edited booking
       */
      classroom: classroom,
    };
    await submitClassroomBookingsMutation(booking, false, true);

    setIsLoading(false);
  };

  const handleCardClick = (classroom: Classroom, requestPath: string) => {
    const hasFullCoverage =
      classroom?.coveragePercentage >= 100 &&
      state?.search?.dateTimeFormatted?.length === classroom?.dateAvailability?.length;

    if (!!state?.search?.currentBooking && hasFullCoverage) {
      handleSubmitRequest(classroom);
      return;
    }

    dispatch({
      type: SearchTypes.SetSelectedClassrooms,
      payload: [
        ...state?.search?.selectedClassrooms,
        { classroom, dates: getClassroomCoveredDates(classroom, state?.search?.dateTimeFormatted) },
      ],
    });

    if (hasFullCoverage) {
      history.push(requestPath);
      return;
    }

    onSelectClassroom?.(classroom);
  };

  const columns: TableColumn[] = [
    {
      key: ClassroomOrderByFields.Name,
      isSortable: true,
      header: t("booking.classroom-search.table.headers.name"),
      renderCell: (classroom: any) => getColumnCell(classroom?.name || "-"),
    },
    {
      key: "classroom-type",
      header: t("booking.classroom-search.table.headers.classroom-type"),
      renderCell: (classroom: any) =>
        getColumnCell(
          <ClassroomCell
            classroom={{
              label: classroom?.classroomType?.name,
              isSpecial: !!classroom?.isSpecialTagValue,
            }}
          />,
        ),
    },
    {
      key: ClassroomOrderByFields.Building,
      isSortable: true,
      header: t("booking.classroom-search.table.headers.building"),
      renderCell: (classroom: any) => getColumnCell(classroom?.building?.name || "-"),
    },
    {
      key: "floor",
      header: t("booking.classroom-search.table.headers.floor"),
      renderCell: (classroom: any) => getColumnCell(classroom?.floor ?? "-"),
    },
    {
      key: ClassroomOrderByFields.Capacity,
      isSortable: true,
      header: t("booking.classroom-search.table.headers.capacity"),
      renderCell: (classroom: any) => getColumnCell(classroom?.capacity ?? "-"),
    },
    {
      key: ClassroomOrderByFields.CoveragePercentage,
      isSortable: true,
      header: t("booking.classroom-search.table.headers.coverage-percentage"),
      renderCell: (classroom: any) => {
        const { color, textColor } = getCoveragePillColors(classroom?.coveragePercentage ?? 0);

        return getPillCell(`${classroom?.coveragePercentage}%`, {
          color,
          textColor,
          iconColor: textColor,
          icon: classroom?.coveragePercentage >= 100 ? "check" : undefined,
        });
      },
    },
  ];

  return (
    <div className={css.cardBooking}>
      {isLoadingRequestOrItems && (
        <DimensionsSearchSkeleton rows={pageSize} columns={columns?.length} showHeader={false} />
      )}

      {!isLoadingRequestOrItems && pageInfo?.total === 0 && (
        <CardNotification state="info" title="No se encontraron coincidencias" outlined>
          Prueba con otros términos en la configuración de reserva.
        </CardNotification>
      )}

      {!isLoadingRequestOrItems && !!items?.length && (
        <Table
          data={items ?? []}
          columns={columns}
          rightActions={[
            {
              renderContent(classroom: any) {
                const requestPath = `/booking/${
                  !isAdminBooking ? "create/" : ""
                }request/${contextUrl}/${classroom.id}${Boolean(bookingId) ? `/${bookingId}` : ""}${
                  !isAdminBooking ? `?scenario=${scenario}&origin=${origin}` : ""
                }`;

                return (
                  <Tooltip
                    label={t("booking.classroom-search.table.tooltip-continue")}
                    placement="topRight"
                  >
                    <RoundButton
                      icon="chevron-right"
                      size="sm"
                      variant="secondary"
                      disabled={!!selectedClassroomsById?.[classroom?.id]}
                      onClick={() => handleCardClick(classroom, requestPath)}
                    />
                  </Tooltip>
                );
              },
            },
          ]}
          initialSort={
            orderBy?.length
              ? {
                  column: orderBy?.[0]?.field,
                  direction: orderBy?.[0]?.direction?.toLowerCase() as "asc" | "desc",
                }
              : undefined
          }
          onSortChange={newShort => {
            onChangeOrderBy([
              {
                field: newShort.column,
                direction: newShort.direction.toUpperCase(),
              },
            ]);
          }}
        />
      )}

      {!isLoadingRequestOrItems && !!items?.length && !!pageInfo && (
        <Pager
          classname={{
            global: css.cardBooking_pager,
          }}
          showRowsPerPage
          page={pageInfo?.page ?? 1}
          size={pageSize ?? 5}
          total={pageInfo?.total ?? 0}
          pageSizeOptions={[5, 10, 20]}
          onChange={onChangePage}
          onRowsPerPageChange={pageSize => {
            onChangePageSize?.(pageSize?.value);
          }}
        />
      )}
    </div>
  );
};

export default CardBooking;
